import AppIconButton from '@nxsys/components/AppIconButton';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultCisStatus,
  defaultPaymentFrequency,
  defaultRegistrationStatus,
  paymentModels,
} from '@nxsys/constants/AppConstants';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { roles } from '@nxsys/store/constants';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { UserType } from '@nxsys/types/models/User';
import { defaultMarginMethod, formatStatus } from '@nxsys/utils';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { ColumnsType } from 'antd/lib/table';
import { filter } from 'lodash';
import moment from 'moment';

export const getEmployeeColumns = ({
  messages,
  user,
  handleResendLoginIdMultiple,
  handleUpdateHoldByEmployee,
  handleAddTimesheet,
  handleViewEmployee,
  handleConfirmDelete,
  isSending,
  selectedResends,
  isAgency,
  isBureau,
  payrollCountry,
  isIreland,
}: {
  messages: TypedMessages;
  user: UserType | null;
  handleResendLoginIdMultiple: (data: EmployeeRecordType[]) => void;
  handleUpdateHoldByEmployee: (employee: EmployeeRecordType) => void;
  handleAddTimesheet: (employee: EmployeeRecordType) => void;
  handleViewEmployee: (employee: EmployeeRecordType) => void;
  handleConfirmDelete: (employee: EmployeeRecordType) => void;
  isSending?: boolean;
  selectedResends?: string[];
  isAgency: boolean;
  isBureau?: boolean;
  payrollCountry?: string;
  isIreland?: boolean;
}): ColumnsType<EmployeeRecordType> => {
  const columns: ColumnsType<EmployeeRecordType> = [
    {
      key: 'actions',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      width: 120,
      render: (_, record) => {
        if (user?.role === roles.agency) {
          return (
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <AppIconButton
                title={messages['common.edit']}
                icon={<Icons.IconEdit />}
                onClick={() => handleViewEmployee(record)}
              />
            </div>
          );
        }

        const isSelectedResend = selectedResends?.includes(record.id);

        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <AppIconButton
              title={messages['common.edit']}
              icon={<Icons.IconEdit />}
              onClick={() => handleViewEmployee(record)}
            />
            <AppIconButton
              title={messages['common.addTimesheet']}
              icon={<Icons.IconFilePlus />}
              onClick={() => handleAddTimesheet(record)}
            />
            <AppIconButton
              title={
                record?.isOnHold
                  ? messages['common.unHold']
                  : messages['common.hold']
              }
              icon={
                <Icons.IconShield color={record?.isOnHold ? 'red' : 'green'} />
              }
              onClick={() => handleUpdateHoldByEmployee(record)}
            />
            <AppIconButton
              title={
                isSending && isSelectedResend
                  ? 'Sending...'
                  : record.errorResendLoginIdFailed
                    ? `Error: ${record.errorResendLoginIdFailed}`
                    : record.isResendLoginId
                      ? 'Resent email successfully.'
                      : 'Resend login Id.'
              }
              icon={
                isSending && isSelectedResend ? (
                  <Icons.IconSend color="#ff9900" />
                ) : (
                  <Icons.IconEmail
                    color={
                      record.errorResendLoginIdFailed
                        ? 'red'
                        : record.isResendLoginId
                          ? '#22bb00'
                          : '#666666'
                    }
                  />
                )
              }
              onClick={() => handleResendLoginIdMultiple([record])}
            />
            <AppIconButton
              title="Delete"
              icon={<Icons.IconTrash color="red" />}
              onClick={() => handleConfirmDelete(record)}
            />
          </div>
        );
      },
    },
    {
      key: 'status',
      title: messages['table.status'],
      dataIndex: 'status',
      align: 'center',
      width: 100,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (status) => (
        <span className={`${formatStatus(status)}`}>
          <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
            {formatStatus(status)}
          </AppTagStatus>
        </span>
      ),
    },
    {
      key: 'id',
      title: messages['table.systemId'],
      dataIndex: 'id',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'employeeName',
      title: messages['table.employeeName'],
      render: (value) =>
        value?.fullName ||
        `${value?.forename || ''} ${value?.surname || ''}` ||
        '_',
      width: isBureau ? 180 : 250,
    },
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['table.agencyRef'],
      width: isBureau ? 120 : 200,
      render: (record) =>
        isBureau
          ? record?.agencyRef || record?.id || '_'
          : `${record?.primaryAgency?.agencyPrefix || '___'}/${
              record?.agencyRef || record?.id || '_'
            }`,
    },
    {
      key: 'companyPayment',
      title: isBureau
        ? messages['table.employer']
        : messages['table.companyPayment'],
      dataIndex: 'companyPayment',
      width: isBureau ? 200 : 250,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'paymentModel',
      title: messages['table.paymentModel'],
      dataIndex: 'setting',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const label =
          paymentModels[value?.paymentModel as keyof typeof paymentModels] ||
          'Not Set';
        return isIreland && label === 'CIS' ? 'RCT' : label;
      },
    },
    {
      key: 'registrationStatus',
      title: messages['table.registrationStatus'],
      dataIndex: 'registrationStatus',
      align: 'center',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        filter(defaultRegistrationStatus, (item) => item.value === value)
          ? filter(defaultRegistrationStatus, (item) => item.value === value)[0]
              ?.label
          : '',
    },
    {
      key: 'payFrequency',
      title: messages['table.payFrequency'],
      dataIndex: 'payFrequency',
      align: 'center',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        filter(defaultPaymentFrequency, (item) => item?.value === value)
          ? filter(defaultPaymentFrequency, (item) => item?.value === value)[0]
              ?.label
          : '',
    },
    ...(!isIreland && !isBureau
      ? [
          {
            key: 'primaryAgency',
            title: messages['table.primaryAgency'],
            dataIndex: 'primaryAgency',
            width: isBureau ? 200 : 250,
            sorter: true,
            sortDirections: ['descend' as const, 'ascend' as const],
            render: (value: any) => value?.name || '_',
          },
        ]
      : []),
    {
      key: 'mobileNo',
      title: messages['table.mobileNo'],
      dataIndex: 'mobile',
      width: 120,
    },
    {
      key: 'emailID',
      title: messages['table.emailId'],
      dataIndex: 'email',
      width: 250,
    },
    {
      key: 'NINo',
      title: getFieldLabelByCountry(payrollCountry ?? '', 'NINo'),
      width: 120,
      render: (record) =>
        isIreland ? record?.setting?.ppsNumber || '' : record.NINumber || '',
    },
    ...(!isIreland && !isBureau
      ? [
          {
            key: 'UTRNo',
            title: messages['table.utrNo'],
            dataIndex: 'setting',
            width: 150,
            render: (value: any) => value?.UTRNo || '_',
          },
        ]
      : []),
    ...(!isBureau
      ? [
          {
            key: 'CISStatus',
            title: isIreland
              ? messages['table.rctStatus']
              : messages['table.cisStatus'],
            dataIndex: 'setting',
            width: 150,
            render: (value: any, record: EmployeeRecordType) => {
              const paymentModel = record?.setting?.paymentModel;
              if (paymentModel !== 'CIS' && paymentModel !== 'NON_CIS') {
                return '-';
              }
              return value && value?.cisStatus
                ? filter(
                    defaultCisStatus,
                    (item) => item.value === value?.cisStatus,
                  )[0]?.label
                : '_';
            },
          },
        ]
      : []),
    ...(!isIreland && !isBureau
      ? [
          {
            key: 'pvNo',
            title: messages['table.pvNo'],
            dataIndex: 'setting',
            width: 150,
            render: (value: any) => value?.personalVerificationNumber || '',
          },
          {
            key: 'Verification Date',
            title: messages['table.verificationDate'],
            dataIndex: 'setting',
            width: 150,
            align: 'right' as const,
            render: (value: any) =>
              value && value?.verificationDate
                ? moment(value?.verificationDate)
                    .tz('Europe/London')
                    .format('DD/MM/YYYY')
                : '_',
          },
          {
            key: 'Match Date',
            title: messages['table.matchDate'],
            dataIndex: 'setting',
            width: 150,
            align: 'right' as const,
            render: (value: any) =>
              value && value?.matchDate
                ? moment(value?.matchDate)
                    .tz('Europe/London')
                    .format('DD/MM/YYYY')
                : '_',
          },
        ]
      : []),
    {
      key: 'marginMethod',
      title: messages['table.marginMethod'],
      dataIndex: 'setting',
      width: 150,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value &&
        value?.marginMethod &&
        filter(
          defaultMarginMethod,
          (item) => item.value === value?.marginMethod,
        )[0]
          ? filter(
              defaultMarginMethod,
              (item) => item.value === value?.marginMethod,
            )[0]?.label
          : '',
    },
    {
      key: 'margin',
      title: messages['table.margin'],
      dataIndex: 'setting',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.margin || '_',
    },
    {
      key: 'marginCapped',
      title: messages['table.marginCap'],
      dataIndex: 'setting',
      width: 120,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.marginCapped || '_',
    },
    ...(!isIreland
      ? [
          {
            key: 'shareCodeVerified',
            title: messages['table.shareCodeVerified'],
            dataIndex: 'shareCodeVerified',
            sorter: true,
            sortDirections: ['descend' as const, 'ascend' as const],
            render: (value: any) => value || '_',
            width: 200,
          },
        ]
      : []),
    {
      key: 'lastPaidDate',
      title: messages['table.lastPaidDate'],
      align: 'right',
      width: 100,
      render: (value) => (
        <div>
          {value?.latestPayroll?.paymentDate
            ? moment(value?.latestPayroll?.paymentDate)
                .tz('Europe/London')
                .format('DD/MM/YYYY')
            : ''}
        </div>
      ),
    },
    {
      key: 'lastPaidPeriod',
      title: messages['table.lastPaidPeriod'],
      align: 'right',
      width: 100,
      render: (value) => (
        <div>
          {value?.latestPayroll
            ? `${value?.latestPayroll?.taxPeriod}/${value?.latestPayroll?.taxYear}`
            : ''}
        </div>
      ),
    },
  ];

  if (isAgency) {
    return [
      {
        key: 'actions',
        title: messages['common.action'],
        fixed: 'left',
        align: 'left',
        width: 120,
        render: (record) => (
          <ul className="table-actions flex items-center justify-start">
            <li>
              <AppIconButton
                title={messages['common.edit']}
                icon={<Icons.IconEdit />}
                onClick={() => handleViewEmployee(record)}
              />
            </li>
          </ul>
        ),
      },
      {
        key: 'status',
        title: messages['table.status'],
        dataIndex: 'status',
        align: 'center',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (status) => (
          <span className={`${formatStatus(status)}`}>
            <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
              {formatStatus(status)}
            </AppTagStatus>
          </span>
        ),
      },
      {
        key: 'id',
        title: messages['table.systemId'],
        dataIndex: 'id',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
      },
      {
        key: 'forename',
        title: messages['table.forename'],
        dataIndex: 'forename',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (value) => value || '_',
      },
      {
        key: 'surname',
        title: messages['table.surname'],
        dataIndex: 'surname',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (value) => value || '_',
      },
      {
        key: 'agencyRef',
        title: messages['table.agencyRef'],
        width: 250,
        render: (record) =>
          `${record?.primaryAgency?.agencyPrefix || '___'}/${
            record?.agencyRef || record?.id || '_'
          }`,
      },
      {
        key: 'registrationStatus',
        title: messages['table.registrationStatus'],
        dataIndex: 'registrationStatus',
        align: 'center',
        sorter: true,
        sortDirections: ['descend', 'ascend'],
        render: (value) =>
          value &&
          filter(defaultRegistrationStatus, (item) => item.value === value)
            ? filter(
                defaultRegistrationStatus,
                (item) => item.value === value,
              )[0]?.label
            : '',
      },
      {
        key: 'mobileNo',
        title: messages['table.mobileNo'],
        dataIndex: 'mobile',
      },
      {
        key: 'emailID',
        title: messages['table.emailId'],
        dataIndex: 'email',
        width: 250,
      },
      {
        key: 'UTRNo',
        title: messages['table.utrNo'],
        dataIndex: 'setting',
        render: (value) => value?.UTRNo || '_',
      },
      {
        key: 'DOB',
        title: messages['table.dob'],
        dataIndex: 'DOB',
        render: (value) =>
          value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      },
      {
        key: 'dateOfJoining',
        title: messages['table.dateOfJoining'],
        dataIndex: 'dateOfJoining',
        render: (value) =>
          value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      },
      {
        key: 'dateLeft',
        title: messages['table.dateLeft'],
        dataIndex: 'dateLeft',
        render: (value) =>
          value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
      },
    ];
  }

  if (isBureau) {
    const payrollHiddenKeys = new Set([
      'primaryAgency',
      'marginMethod',
      'margin',
      'marginCapped',
    ]);
    return columns.filter((col) => !payrollHiddenKeys.has(col.key as string));
  }

  return columns;
};
